import { useState } from 'react';
import { Stack } from 'react-bootstrap';
import { icons } from '../../assets';
import { BigButton, Loader, ModalButtonArea } from '../../common';
import { getThumbnail } from '../../common/MyProductsTextureEdit/textureEditConfig';
import MyDropzone from '../../components/MyDropzone';
import {
  MAX_UPLOAD_HEIGHT_PIXELS,
  MAX_UPLOAD_WIDTH_PIXELS,
} from '../../constants/textureConstants';
import {
  apiBulkUploadProducts,
  apiPostImageOnPresignedData,
  DeleteProductAll360Images,
} from '../../helpers/api';
import {
  compare,
  duplicateArrayRemove,
  getFormData,
  getImageDetails,
  removeSpecialCharacters,
  roundToTwo,
} from '../../helpers/jsHelper';
import { errorToast } from '../../helpers/toastHelper';

const { Close, File } = icons;

export const UploadThreeSixtyImage = (props) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [btnTitle, setBtnTitle] = useState('Upload');
  const [allSelectedFiles, setAllSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const sortSelectedFiles = () => {
    const filesArray = Array.from(selectedFiles);
    const sortedFilesArray = filesArray?.sort(compareItems) || [];
    return sortedFilesArray;
  };

  const compareItems = (item1, item2) => {
    const num1 = parseInt(item1?.name?.match(/\d+/)?.[0], 10); // Extract and parse the numeric part from item1
    const num2 = parseInt(item2?.name?.match(/\d+/)?.[0], 10); // Extract and parse the numeric part from item2
    return num1 - num2; // Sort in ascending order, use `num2 - num1` for descending order
  };

  const selectedFilesSort = sortSelectedFiles();

  async function isFilePresent(file) {
    if (selectedFiles.length === 0) return Promise.resolve(false);

    const res = await Promise.all(selectedFiles.map((sf) => compare(sf.file, file)));
    const x = res.every((r) => r === false);
    return !x;
  }

  const handleFileChange = (files) => {
    setAllSelectedFiles((prevFiles) =>
      duplicateArrayRemove([...prevFiles, ...files.map((a) => a?.name)])
    );

    files.forEach((file) => {
      isFilePresent(file).then((res) => {
        if (res === true) {
          errorToast(`This file named "${file?.name}" has already been uploaded`);
          return;
        }

        var fileName = removeSpecialCharacters(file.name);
        const src = URL.createObjectURL(file);
        getImageDetails(src)
          .then((res) => {
            const { width, height, dpi } = res;
            if (width > MAX_UPLOAD_WIDTH_PIXELS || height > MAX_UPLOAD_HEIGHT_PIXELS) {
              URL.revokeObjectURL(src);
              errorToast('The image you have uploaded is above 16k resolution');
              return;
            }
            getThumbnail(file).then((tFile) => {
              const uploadFile = {
                id: `id-${new Date().getTime()}`,
                name: fileName,
                sku_id: '',
                width: roundToTwo(width / dpi),
                height: roundToTwo(height / dpi),
                rotation: 0,
                description: '',
                filteroptions: [],
                file: file,
                texture_file: src,
                thumbnail: tFile,
                fileWithExtension: file?.name,
              };
              setSelectedFiles((prevFiles) => [uploadFile, ...prevFiles]);
            });
          })
          .catch(() => {
            const errorMessage =
              files.length > 1
                ? 'Some of the textures you are trying to upload are above 16k resolution'
                : 'The texture you are trying to upload is above 16k resolution';
            errorToast(errorMessage);
          });
      });
    });
  };

  const handleFileDelete = (fileId) => {
    const updatedFiles = selectedFilesSort.filter((x) => x.id !== fileId);
    const fileToDelete = selectedFilesSort.find((x) => x.id === fileId);
    setSelectedFiles(updatedFiles);
    setAllSelectedFiles(allSelectedFiles.filter((a) => a !== fileToDelete?.fileWithExtension));
  };


  const handleFileImport = async () => {
    if (loading) {
      return;
    }
    setBtnTitle('Please wait!!!');
    const texturesForUpload = selectedFilesSort.map(({ file }, index) => {
      return {
        product: props?.productId,
        version: 1,
        image_file_url_key: `${index + 1}.${file?.name?.split('.').pop()}`,
      };
    });
    setLoading(true);
    if (props?.productId) {
      await DeleteProductAll360Images(props?.productId);
    }
    callAPIsSequentially(texturesForUpload);
  };

  async function callAPIsSequentially(apiUrls) {
    try {
      for (const [index, apiUrl] of apiUrls.entries()) {
        let payload = { ...apiUrl };
        delete payload.name;
        const formData = getFormData([payload]);
        const res = await apiBulkUploadProducts(formData, null, null)
          .then(async (res) => {
            if (res && res?.status) {
              if (!Object.keys(res?.data)?.length) return;
              const { id, presigned_data, presigned_url } = res?.data;
              if (presigned_data) {
                const updatedData = {
                  ...selectedFilesSort[index],
                  id: id,
                  is_uploaded: false,
                  version: 1,
                  presigned_data,
                  presigned_url,
                };
                if (updatedData) {
                  await apiPostImageOnPresignedData(
                    selectedFilesSort[index]?.file,
                    presigned_data,
                    () => {}
                  );
                }
              }
            }
          })
          .finally(() => {
            if (index + 1 === apiUrls?.length) {
              // setLoading(false);
              // setBtnTitle('Uploaded');
              props?.handleCloseModal?.({ refresh: true });
            }
          });
      }
    } catch (error) {
      setLoading(false);
      console.error('Error occurred while uploading image:', error);
    }
  }

  const checkForDisable = () => {
    if (selectedFiles.length !== allSelectedFiles.length) {
      return true;
    } else {
      return false;
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    setAllSelectedFiles([]);
  };

  return (
    <ModalButtonArea
      {...props}
      size="md"
      centered={true}
      bigButton={false}
      bigIcon={true}
      variant="link"
      buttonTitle={props.buttonTitle}
      iconBefore={props.iconBefore}
      onClose={handleClose}
      checkForPermission="UPLOAD_TEXTURE"
      className="upload-texture-modal"
      disabled={props.disableButton}
      backdropClassName="overlay-dark"
      headingLeft
      {...(selectedFiles?.length !== 0 && { backdrop: 'static' })}
      title={
        <>
          <h4>Upload Image</h4>
        </>
      }
      body={
        <>
          <MyDropzone
            v2UploadTextures
            subTitle="(.jpg, .png files supported. Max resolution 16k) "
            onDrop={handleFileChange}
          />
          <p className="dz-sub-title small mb-0 mt-1">
            *When you upload new images then all current images are deleted.
          </p>
          {selectedFiles.length > 0 && (
            <Stack>
              <div
                style={{
                  position: 'relative',
                  maxHeight: 225,
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  marginTop: '1.5rem',
                  marginBottom: '2rem',
                }}>
                <table className="bordered">
                  <tbody>
                    {selectedFilesSort?.map((file, index) => (
                      <tr key={index}>
                        <td>
                          <span
                            style={{
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}>
                            <File className="me-2" /> {file.name}
                          </span>
                          <span
                            onClick={() => handleFileDelete(file.id)}
                            className="close-upload-file">
                            <Close />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="file-selected-upload">
                <div>
                  <p>{selectedFiles ? selectedFiles.length : 0} files selected</p>
                  {checkForDisable() && (
                    <p
                      className="d-flex gap-2 justify-content-between"
                      style={{ position: 'relative' }}>
                      <span>Loading files</span>{' '}
                      <Loader loading size={16} thickness={1} position={'relative'} />
                    </p>
                  )}
                </div>
                <BigButton
                  className="lf-btn-1"
                  buttonTitle={btnTitle}
                  onClick={handleFileImport}
                  disabled={checkForDisable() || loading}
                />
              </div>
            </Stack>
          )}
        </>
      }
    />
  );
};
